/**
 * ExplanationConsolePage — the ai-explanation entry surface (/ai-explanation). A keyboard-first by-id
 * lookup: enter a compte-rendu id (a signed/emitted report) and open its explanation panel. The panel
 * itself owns the read/generate/regenerate flow and every degrade path (cloud-only unavailable,
 * graceful indisponible), so this screen stays a thin, always-available launcher.
 *
 * States: sans-droit is enforced route-level (ProtectedRoute on the module's read cap) — reaching here
 * already means the user may read; the "no CR selected yet" baseline is the EmptyState (vide). There's
 * no list endpoint to page, so loading/error/offline live on the panel, not here.
 */
import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Card, EmptyState, FormField, Input } from "@/ui";
import { useT } from "@/app";
import { PageHeader } from "../components/PageHeader";
import { DisclaimerBlock } from "../components/DisclaimerBlock";

export function ExplanationConsolePage() {
  const { t } = useT();
  const navigate = useNavigate();
  const [crId, setCrId] = useState("");

  const trimmed = crId.trim();
  const open = (e: FormEvent) => {
    e.preventDefault();
    if (!trimmed) return;
    navigate(`/ai-explanation/${encodeURIComponent(trimmed)}`);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-5)" }}>
      <PageHeader title={t("aix.console.title")} subtitle={t("aix.console.subtitle")} />

      <DisclaimerBlock />

      <Card title={t("aix.console.title")}>
        <form
          onSubmit={open}
          style={{ display: "flex", alignItems: "flex-end", gap: "var(--nv-s-3)", flexWrap: "wrap" }}
        >
          <div style={{ flex: "1 1 16rem", minWidth: 0 }}>
            <FormField label={t("aix.console.lookup.label")} hint={t("aix.console.lookup.hint")}>
              {(p) => (
                <Input
                  {...p}
                  mono
                  value={crId}
                  inputMode="numeric"
                  placeholder={t("aix.console.lookup.placeholder")}
                  onChange={(e) => setCrId(e.target.value)}
                />
              )}
            </FormField>
          </div>
          <Button type="submit" disabled={!trimmed} leadingIcon="✦">
            {t("aix.console.open")}
          </Button>
        </form>
      </Card>

      <EmptyState
        glyph="✦"
        title={t("aix.console.empty.title")}
        description={t("aix.console.empty.desc")}
      />
    </div>
  );
}
